/* ==========================================================================
   Scirocco — pages.js (area pagine: Chi siamo, Materiali, Contatti, blog)
   Usa le API di theme.js (window.SC). Ogni parte non fa nulla se i suoi elementi mancano.

   <sc-tabs>   tab accessibili (pattern ARIA "tabs", attivazione automatica):
               figli [role=tablist] > [role=tab][aria-controls] + pannelli [role=tabpanel].
               Frecce ←/→, Home, Fine; tabindex a rotazione; dissolvenza del pannello (.is-entering).
               Evento "sc:tabs:change" {tab, panel}. Nell'editor segue la selezione del blocco.
   <sc-steps>  passaggi di un materiale: bottoni [data-step] → foto [data-step-shot],
               testo [data-step-text], pillola [data-step-label]. Dissolvenza incrociata in CSS.
               Se un passaggio ha un video, va solo mentre e' quello a schermo.
   Form contatti: stato "invio in corso" sul bottone (l'invio resta quello nativo di Shopify).
   ========================================================================== */
(() => {
  'use strict';
  const SC = (window.SC = window.SC || {});
  const define = (tag, cls) => {
    if (SC.define) SC.define(tag, cls);
    else if (!customElements.get(tag)) customElements.define(tag, cls);
  };

  /* riavvia un'animazione CSS di ingresso su un elemento */
  const replay = (el, cls) => {
    if (!el) return;
    el.classList.remove(cls);
    void el.offsetWidth;
    el.classList.add(cls);
    const done = (e) => {
      if (e.target !== el) return;
      el.classList.remove(cls);
      el.removeEventListener('animationend', done);
    };
    el.addEventListener('animationend', done);
    // rete di sicurezza se l'animazione non parte (riduci movimento, editor)
    setTimeout(() => el.classList.remove(cls), 900);
  };

  /* ---------- <sc-tabs> ---------- */
  class ScTabs extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.tabs = Array.from(this.querySelectorAll('[role="tab"]')).filter((t) => t.closest('sc-tabs') === this);
      if (!this.tabs.length) return;
      this.addEventListener('click', (e) => {
        const tab = e.target.closest('[role="tab"]');
        if (tab && this.tabs.includes(tab)) this.select(tab, { focus: false });
      });
      this.addEventListener('keydown', (e) => this.onKey(e));
    }

    panelOf(tab) {
      const id = tab && tab.getAttribute('aria-controls');
      return id ? document.getElementById(id) : null;
    }

    select(tab, { focus = true, animate = true } = {}) {
      if (!tab) return;
      if (tab.getAttribute('aria-selected') === 'true') {
        if (focus) tab.focus();
        return;
      }
      this.tabs.forEach((t) => {
        const on = t === tab;
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        const p = this.panelOf(t);
        if (p) p.hidden = !on;
      });
      if (focus) tab.focus();
      const panel = this.panelOf(tab);
      if (panel && animate) replay(panel, 'is-entering');
      this.dispatchEvent(new CustomEvent('sc:tabs:change', { bubbles: true, detail: { tab, panel } }));
    }

    onKey(e) {
      const tab = e.target.closest('[role="tab"]');
      if (!tab || !this.tabs.includes(tab)) return;
      const i = this.tabs.indexOf(tab);
      const n = this.tabs.length;
      let next = null;
      if (e.key === 'ArrowRight') next = this.tabs[(i + 1) % n];
      else if (e.key === 'ArrowLeft') next = this.tabs[(i - 1 + n) % n];
      else if (e.key === 'Home') next = this.tabs[0];
      else if (e.key === 'End') next = this.tabs[n - 1];
      if (!next) return;
      e.preventDefault();
      this.select(next);
    }
  }
  define('sc-tabs', ScTabs);

  /* ---------- <sc-steps> ---------- */
  class ScSteps extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;
      this.addEventListener('click', (e) => {
        const btn = e.target.closest('[data-step]');
        if (btn && this.contains(btn)) this.show(btn.getAttribute('data-step'));
      });
      this.syncVideo();
      // tornando sulla scheda del browser il video era rimasto fermo: si riparte da soli
      if (this.querySelector('[data-step-shot] video')) {
        this._onVisible = () => {
          if (document.visibilityState === 'visible') this.syncVideo();
        };
        document.addEventListener('visibilitychange', this._onVisible);
      }
    }

    disconnectedCallback() {
      if (this._onVisible) document.removeEventListener('visibilitychange', this._onVisible);
    }

    /* Il video di un passaggio va solo mentre quel passaggio e' a schermo: negli altri resta
       fermo al primo fotogramma. Con "Riduci movimento" non parte da solo e si mostrano i
       comandi, cosi' chi vuole guardarlo lo avvia a mano. */
    syncVideo() {
      const reduced = SC.reduced ? SC.reduced() : window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      this.querySelectorAll('[data-step-shot] video').forEach((v) => {
        const on = v.closest('[data-step-shot]').classList.contains('is-active');
        if (reduced) {
          v.controls = true;
          if (!on) v.pause();
          return;
        }
        if (on) {
          const p = v.play();
          if (p && p.catch) p.catch(() => { v.controls = true; });
        } else {
          v.pause();
        }
      });
    }

    show(n) {
      const step = String(n);
      let label = '';
      this.querySelectorAll('[data-step]').forEach((b) => {
        const on = b.getAttribute('data-step') === step;
        b.setAttribute('aria-pressed', on ? 'true' : 'false');
        if (on) label = b.getAttribute('data-label') || '';
      });
      this.querySelectorAll('[data-step-shot]').forEach((s) => {
        const on = s.getAttribute('data-step-shot') === step;
        s.classList.toggle('is-active', on);
        if (on) s.removeAttribute('aria-hidden');
        else s.setAttribute('aria-hidden', 'true');
      });
      this.querySelectorAll('[data-step-text]').forEach((p) => {
        const on = p.getAttribute('data-step-text') === step;
        p.hidden = !on;
        if (on) replay(p, 'is-entering');
      });
      const pill = this.querySelector('[data-step-label]');
      if (pill && this.querySelector('[data-step]')) {
        pill.textContent = label;
        pill.hidden = !label;
        replay(pill, 'is-entering');
      }
      this.syncVideo();
    }
  }
  define('sc-steps', ScSteps);

  /* cambio materiale: si riparte dal primo passaggio (come nel mockup) */
  document.addEventListener('sc:tabs:change', (e) => {
    const panel = e.detail && e.detail.panel;
    const steps = panel && panel.querySelector('sc-steps');
    if (steps && typeof steps.show === 'function') steps.show(1);
  });

  /* editor del tema: selezionando un blocco "Materiale" si apre la sua tab */
  document.addEventListener('shopify:block:select', (e) => {
    const panel = e.target && e.target.closest ? e.target.closest('[role="tabpanel"]') : null;
    const host = panel && panel.closest('sc-tabs');
    if (!host || typeof host.select !== 'function') return;
    const tab = host.querySelector(`[aria-controls="${CSS.escape(panel.id)}"]`);
    if (tab) host.select(tab, { focus: false, animate: false });
  });

  /* ---------- Form contatti: stato di invio ---------- */
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.classList.contains('sc-contact__form')) return;
    const btn = form.querySelector('button[type="submit"]');
    if (!btn || e.defaultPrevented) return;
    btn.setAttribute('data-state', 'loading');
    btn.setAttribute('aria-busy', 'true');
  });
  // tornando indietro dalla cache del browser il bottone non deve restare "in invio"
  window.addEventListener('pageshow', (e) => {
    if (!e.persisted) return;
    document.querySelectorAll('.sc-contact__form button[data-state="loading"]').forEach((b) => {
      b.setAttribute('data-state', 'idle');
      b.removeAttribute('aria-busy');
    });
  });
})();
